import type { TFunction } from 'i18next';
import { WorkEnvironmentSurveyStatus, type AreaLeft } from '../../api/workEnvironmentTypes';
import { fullDate } from '../HRDashboard/hmsFormat';
import { planAreaPath, SurveySection } from './surveyRoutes';

/*
 * The areas the inspection report still waits for come from the API, which uses the same rule as
 * the report: high risk needs an approved plan, moderate risk a plan or a decision.
 */

const K = 'workEnvironmentAreasLeft';

/** Plans are made and changed in the plan step and while they are followed up, as the API allows */
export const plansCanChange = (status: WorkEnvironmentSurveyStatus) =>
  status === WorkEnvironmentSurveyStatus.ACTION_PLAN_PENDING ||
  status === WorkEnvironmentSurveyStatus.ACTION_PLAN_COMPLETED;

const LEVEL_ORDER: Record<AreaLeft['level'], number> = { high: 0, moderate: 1 };

const time = (deadline: string | null) => (deadline ? new Date(deadline).getTime() : Infinity);

/** High risk first, then the earliest deadline */
export const byRiskAndDeadline = (areas: AreaLeft[]) =>
  [...areas].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || time(a.deadline) - time(b.deadline));

/** «Frist 2. november 2026.», or «Fristen gikk ut 2. november 2026.» once it has passed */
export const deadlineText = (area: AreaLeft, t: TFunction, language: string) =>
  area.deadline ? t(`${K}.${area.overdue ? 'overdue' : 'due'}`, { date: fullDate(area.deadline, language) }) : '';

/**
 * «Vold: høy risiko, mangler godkjent tiltaksplan. Frist 2. november 2026.», or as «… mangler
 * fortsatt …» once another plan is approved
 */
export const areaLeftText = (area: AreaLeft, t: TFunction, language: string, form: 'row' | 'still' = 'row') =>
  [t(`${K}.${form}.${area.level}`, { name: area.name }), deadlineText(area, t, language)].filter(Boolean).join(' ');

export type AreaLeftRow = {
  key: string;
  name: string;
  text: string;
  overdue: boolean;
  /** Step 7 of the survey at the area, for those who make plans */
  plan: string | null;
  /** A decision is enough only for moderate risk */
  decision: string | null;
};

export const areaLeftRows = (
  areas: AreaLeft[],
  survey: { id: number; canPlan: boolean },
  section: SurveySection,
  t: TFunction,
  language: string,
): AreaLeftRow[] =>
  byRiskAndDeadline(areas).map((area) => ({
    key: area.key,
    name: area.name,
    text: areaLeftText(area, t, language),
    overdue: area.overdue,
    plan: survey.canPlan ? planAreaPath(section, survey.id, area.key) : null,
    decision: survey.canPlan && area.level === 'moderate' ? planAreaPath(section, survey.id, area.key, true) : null,
  }));
